import React, { useState, useRef, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getProductById, allProducts } from "../data/index";
import "./ProductDetail.css";

const optimizeUrl = (url) => {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  return url.replace("/upload/", "/upload/q_auto,f_auto,/");
};

const optimizeThumbUrl = (url) => {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  return url.replace("/upload/", "/upload/q_auto,f_auto,/");
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = getProductById(id);
  const [zoomed, setZoomed] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [lightbox, setLightbox] = useState(false);
  const imgRef = useRef(null);

  if (!product) {
    return (
      <div>
        <Helmet>
          <title>Product Not Found | RK Overseas</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="page-banner">
          <h1>Product Not Found</h1>
        </div>
        <div
          className="container"
          style={{ padding: "80px 24px", textAlign: "center" }}
        >
          <p>The product you are looking for does not exist.</p>
          <Link
            to="/product"
            className="btn-primary"
            style={{ marginTop: "24px", display: "inline-block" }}
            aria-label="Back to all products"
          >
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const related = allProducts
    .filter(
      (p) => p.category === product.category && p.globalId !== product.globalId,
    )
    .slice(0, 4);

  const handleMouseMove = (e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };

  const openLightbox = () => {
    setLightbox(true);
    document.body.style.overflow = "hidden";
    window.history.pushState({ lightbox: true }, "");
  };

  const closeLightbox = () => {
    setLightbox(false);
    document.body.style.overflow = "unset";
  };

  useEffect(() => {
    const handlePopState = () => {
      if (lightbox) closeLightbox();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [lightbox]);

  return (
    <div className="product-detail-page">
      <Helmet>
        <title>
          {product.name} | RK Overseas — {product.category} Firozabad, India
        </title>
        <meta
          name="description"
          content={`${product.name} — handcrafted ${product.category.toLowerCase()} by RK Overseas, Firozabad, India. Mouth-blown glass, worldwide export available. Enquire now for bulk orders.`}
        />
        <meta
          name="keywords"
          content={`${product.name}, ${product.category.toLowerCase()} india, handcrafted glass ${product.category.toLowerCase()}, ${product.category.toLowerCase()} firozabad, glass exporter india`}
        />
        <link
          rel="canonical"
          href={`https://rkoverseasindia.com/product/${product.globalId}`}
        />
        {/* Open Graph for social sharing */}
        <meta property="og:title" content={`${product.name} | RK Overseas`} />
        <meta
          property="og:description"
          content={`Handcrafted ${product.category} by RK Overseas, Firozabad, India. Worldwide export available.`}
        />
        <meta property="og:image" content={product.imageUrl} />
        <meta
          property="og:url"
          content={`https://rkoverseasindia.com/product/${product.globalId}`}
        />
        <meta property="og:type" content="product" />
      </Helmet>

      {lightbox && (
        <div className="pd-lightbox" onClick={closeLightbox}>
          <button
            className="pd-lightbox__close"
            onClick={closeLightbox}
            aria-label="Close image viewer"
          >
            ✕
          </button>
          <img
            src={product.imageUrl}
            alt={product.name}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="product-detail__breadcrumb">
        <div className="container">
          <Link to="/" aria-label="Go to homepage">
            Home
          </Link>
          <span>/</span>
          <Link to="/product" aria-label="Go to all products">
            Products
          </Link>
          <span>/</span>
          <Link
            to={"/product?category=" + encodeURIComponent(product.category)}
            aria-label={`View all ${product.category} products`}
          >
            {product.category}
          </Link>
          <span>/</span>
          <span className="product-detail__breadcrumb-current">
            {product.name}
          </span>
        </div>
      </div>

      <section className="section product-detail__main">
        <div className="container">
          <div className="product-detail__grid">
            <div className="product-detail__image-col">
              <div
                className={
                  "product-detail__img-wrap" + (zoomed ? " zoomed" : "")
                }
                onMouseEnter={() => setZoomed(true)}
                onMouseLeave={() => setZoomed(false)}
                onMouseMove={handleMouseMove}
                onClick={openLightbox}
                ref={imgRef}
                style={
                  zoomed
                    ? {
                        "--zoom-x": zoomPos.x + "%",
                        "--zoom-y": zoomPos.y + "%",
                      }
                    : {}
                }
              >
                <img src={optimizeUrl(product.imageUrl)} alt={product.name} />
                {!zoomed && (
                  <div className="product-detail__zoom-hint">
                    <span className="hint-desktop">Hover to zoom</span>
                    <span className="hint-mobile">Tap to view</span>
                  </div>
                )}
              </div>
            </div>

            <div className="product-detail__info-col">
              <p className="product-detail__category-tag">{product.category}</p>
              <h1 className="product-detail__name">{product.name}</h1>
              <div className="product-detail__line" />
              <p className="product-detail__subtitle">
                Handcrafted with precision by master artisans in Firozabad,
                India — the glass city of the world. Each piece represents a
                perfect blend of traditional craftsmanship and contemporary
                design.
              </p>

              <div className="product-detail__meta">
                <div className="product-detail__meta-item">
                  <span className="product-detail__meta-label">Category</span>
                  <span className="product-detail__meta-value">
                    {product.category}
                  </span>
                </div>
                <div className="product-detail__meta-item">
                  <span className="product-detail__meta-label">Material</span>
                  <span className="product-detail__meta-value">
                    Mouth-blown Glass
                  </span>
                </div>
                <div className="product-detail__meta-item">
                  <span className="product-detail__meta-label">Origin</span>
                  <span className="product-detail__meta-value">
                    Firozabad, India
                  </span>
                </div>
                <div className="product-detail__meta-item">
                  <span className="product-detail__meta-label">Export</span>
                  <span className="product-detail__meta-value product-detail__in-stock">
                    Available Worldwide
                  </span>
                </div>
              </div>

              <div className="product-detail__actions">
                <Link
                  to="/contact"
                  className="btn-primary"
                  aria-label={`Enquire about ${product.name}`}
                >
                  Enquire Now
                </Link>
                <button
                  className="btn-outline"
                  onClick={() => navigate(-1)}
                  aria-label="Go back to previous page"
                >
                  Back
                </button>
              </div>

              <div className="product-detail__features">
                <div className="product-detail__feature">
                  <span>🏭</span>
                  <span>In-house manufactured</span>
                </div>
                <div className="product-detail__feature">
                  <span>✈️</span>
                  <span>Worldwide export available</span>
                </div>
                <div className="product-detail__feature">
                  <span>🎨</span>
                  <span>Custom designs on request</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section
          className="section product-detail__related"
          style={{ background: "var(--cream)" }}
        >
          <div className="container">
            <h2 className="section-title">More from {product.category}</h2>
            <div
              className="product-detail__line"
              style={{ width: 60, margin: "14px auto 36px" }}
            />
            <div className="product-detail__related-grid">
              {related.map((p) => (
                <Link
                  to={"/product/" + p.globalId}
                  className="products-page__card"
                  key={p.globalId}
                  aria-label={`View details of ${p.name}`}
                >
                  <div className="products-page__img-wrap">
                    <img
                      src={optimizeThumbUrl(p.imageUrl)}
                      alt={p.name}
                      loading="lazy"
                    />
                    <div className="products-page__hover">
                      <span>View Details</span>
                    </div>
                  </div>
                  <div className="products-page__info">
                    <h3>{p.name}</h3>
                    <p className="products-page__cat">{p.category}</p>
                  </div>
                </Link>
              ))}
            </div>
            <div style={{ textAlign: "center", marginTop: "36px" }}>
              <Link
                to={"/product?category=" + encodeURIComponent(product.category)}
                className="btn-outline"
                aria-label={`View all ${product.category} products`}
              >
                View All {product.category}
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
